import { useEffect, useRef, useState } from 'react'
import { AdManagementService, type PexelsImage, type ReferenceImage } from '../../client'

export const MIN_QUERY = 3
const SEARCH_DELAY = 400

type Args = {
  brandId: number
  images: ReferenceImage[]
  selected: string[]
  maxSelected: number
  onImagesChange: (images: ReferenceImage[]) => void
  onSelectedChange: (urls: string[]) => void
}

/** The reference-image actions shared by the generate dialog's manager and the chat popover:
 * select, upload, delete, stock search and stock import. */
export function useReferenceLibrary({
  brandId,
  images,
  selected,
  maxSelected,
  onImagesChange,
  onSelectedChange,
}: Args) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PexelsImage[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [importing, setImporting] = useState<string | null>(null)
  const [error, setError] = useState('')
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => clearTimeout(debounce.current ?? undefined), [])

  function add(url: string) {
    if (selected.length < maxSelected) onSelectedChange([...selected, url])
  }

  function toggle(url: string) {
    if (selected.includes(url)) {
      onSelectedChange(selected.filter((item) => item !== url))
      return
    }
    if (selected.length >= maxSelected) {
      setError(`Max ${maxSelected} reference images can be selected`)
      return
    }
    setError('')
    onSelectedChange([...selected, url])
  }

  async function upload(file: File) {
    setUploading(true)
    setError('')
    try {
      const { gcs_url } = await AdManagementService.uploadReference(brandId, { file })
      onImagesChange([{ gcs_url }, ...images])
      add(gcs_url)
    } catch {
      setError('Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  async function remove(url: string) {
    try {
      await AdManagementService.removeReference(brandId, { gcs_url: url })
      onImagesChange(images.filter((image) => image.gcs_url !== url))
      onSelectedChange(selected.filter((item) => item !== url))
    } catch {
      setError('Failed to remove image')
    }
  }

  function search(next: string) {
    setQuery(next)
    clearTimeout(debounce.current ?? undefined)

    if (next.trim().length < MIN_QUERY) {
      setResults([])
      setSearching(false)
      setSearched(false)
      return
    }

    setSearching(true)
    debounce.current = setTimeout(async () => {
      try {
        const { images: found } = await AdManagementService.pexelsSearch(next.trim(), brandId)
        setResults(found ?? [])
      } catch {
        setError('Image search failed. Please try again.')
        setResults([])
      } finally {
        setSearching(false)
        setSearched(true)
      }
    }, SEARCH_DELAY)
  }

  function resetSearch() {
    setQuery('')
    setResults([])
    setSearched(false)
  }

  async function importImage(image: PexelsImage) {
    setImporting(image.url)
    try {
      const { gcs_url } = await AdManagementService.pexelsImport(brandId, { image_url: image.url })
      onImagesChange([{ gcs_url }, ...images])
      add(gcs_url)
      return true
    } catch {
      setError('Failed to import image')
      return false
    } finally {
      setImporting(null)
    }
  }

  return {
    query,
    results,
    searching,
    searched,
    uploading,
    importing,
    error,
    setError,
    toggle,
    upload,
    remove,
    search,
    resetSearch,
    importImage,
  }
}
